// Spor A block 2 T2.6 (SUP-S 4.2): 'Dan SUP-udtræk' helpers for Standarder → SUP.
// The file name and file are stored with the CPR as markers (##CPR10## in the name,
// ##CPR##, DDMMYY-NNNN, in the file). They are put back here, in the admin's browser,
// after an audited decrypt-cpr; nothing CPR-bearing is logged.

export const SUP_CPR_MARKER = '##CPR##';
export const SUP_CPR_DIGITS_MARKER = '##CPR10##';
export const SUP_TRANSACTION_TYPES = ['Opdater', 'OpdaterSpecielt', 'SletSpecielt', 'Slet'] as const;
export type SupTransactionType = (typeof SUP_TRANSACTION_TYPES)[number];

/** Short Danish description per TransaktionsType ("Den Gode E-journal Dataleverance" 2.2). */
export const SUP_TRANSACTION_LABELS: Record<SupTransactionType, string> = {
  Opdater: 'Opdater (fuldt udtræk)',
  OpdaterSpecielt: 'OpdaterSpecielt (delta)',
  SletSpecielt: 'SletSpecielt (slet ét valgt forløb)',
  Slet: 'Slet (slet alle data for patienten)',
};

/** A forløb the admin can choose (communicate-sup action "forloeb"). */
export type SupForloebOption = {
  id: string;
  /** course = a Clinio referral; earlier-export = only found in an earlier SUP file. */
  kind: 'course' | 'earlier-export';
  start: string | null;
  label: string;
  sent_before: boolean;
};

/** Protocol 2.7: with an earlier export the patient's data is updated (OpdaterSpecielt). */
export function supDefaultTransactionType(earlierExports: number): SupTransactionType {
  return earlierExports > 0 ? 'OpdaterSpecielt' : 'Opdater';
}

/** "01.09.2026 – Knæsmerter (sendt før)"; forløb only known from earlier files show their id. */
export function supForloebLabel(f: SupForloebOption): string {
  const date = f.start
    ? new Date(f.start).toLocaleDateString('da-DK', { timeZone: 'Europe/Copenhagen', day: '2-digit', month: '2-digit', year: 'numeric' })
    : '';
  const what = f.kind === 'course' ? f.label || 'Henvisning' : f.label || `Forløb ${f.id.slice(0, 8)}`;
  const suffix = f.kind === 'earlier-export' ? ' (kun i tidligere udtræk)' : f.sent_before ? ' (sendt før)' : '';
  return `${date ? `${date} – ` : ''}${what}${suffix}`;
}

export type SupExportRow = {
  id: string;
  created_at: string;
  patient_id: string;
  transaction_type: SupTransactionType;
  priority: string;
  persons_count: number;
  extract_id: string;
  extract_id_is_test: boolean;
  file_name_masked: string;
  size_bytes: number;
  xsd_ok: boolean;
  xsd_errors: string[];
};

/** DDMMYY-NNNN and 10 digits from a CPR in either form; null when it is not a 10-digit CPR. */
function cprForms(cpr: string): { hyphen: string; digits: string } | null {
  const digits = cpr.trim().replace(/^(\d{6})-(\d{4})$/, '$1$2');
  if (!/^\d{10}$/.test(digits)) return null;
  return { hyphen: `${digits.slice(0, 6)}-${digits.slice(6)}`, digits };
}

/** Puts the CPR back into a masked file name or file. Returns null when the CPR is not usable. */
export function unmaskSup(text: string, cpr: string): string | null {
  const forms = cprForms(cpr);
  if (!forms) return null;
  return text.split(SUP_CPR_DIGITS_MARKER).join(forms.digits).split(SUP_CPR_MARKER).join(forms.hyphen);
}

/** The file name as shown before the CPR is read: the CPR part is hidden. */
export function hiddenSupFileName(masked: string, hidden = '••••••••••'): string {
  return masked.split(SUP_CPR_DIGITS_MARKER).join(hidden);
}

/** The visible Danish reason for a refused export; E-CPR gets its own text (SUP-S 2.46). */
export function supRefusalMessage(err: { code?: string; message?: string; details?: unknown } | null | undefined): string {
  const details = (err?.details ?? {}) as { code?: unknown };
  const message = (err?.message ?? '').trim();
  if (details.code === 'ecpr') {
    return `SUP-udtrækket er afvist: patienten har et erstatnings-CPR (E-CPR). E-journal modtager ikke data for erstatnings-CPR, så der er ikke dannet nogen fil.${message ? ` (${message})` : ''}`;
  }
  if (err?.code === 'invalid-input' && message) return `SUP-udtrækket er afvist: ${message}`;
  if (err?.code === 'forbidden') return 'Kun administratorer kan danne SUP-udtræk.';
  return message || 'SUP-udtrækket kunne ikke dannes.';
}

export function formatSupTime(iso: string): string {
  const date = new Date(iso);
  return Number.isNaN(date.getTime())
    ? iso
    : date.toLocaleString('da-DK', { timeZone: 'Europe/Copenhagen', dateStyle: 'short', timeStyle: 'medium' });
}
